import { useState } from 'react';
import { useActivation } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import {
  NOT_ENOUGH, confidenceCell, droppedCell, liftCell, ratePct, reachedCell, sideCell, sortActions, worstSurfaceStep,
  type AhaOutcome, type SurfaceFunnelWindow,
} from '@/lib/growth-activation';
import { num, when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * Two panels on Growth (GROWTH_OPERATING_SYSTEM_2026-10-21, first slice).
 *
 * "Where people stop, per surface": the seven milestones as columns, one
 * row for everyone and one per signup surface (Mac browser, iPhone browser,
 * the phone apps ...), over 7, 30 or 90 days. Each cell is a count; the
 * percent of the surface's signups rides along only when the surface has
 * ten or more, and the loss between steps is printed under the count. The
 * header names the single worst loss point across surfaces.
 *
 * "What predicts coming back": for everyone whose day 30 (or day 7) window
 * has closed, each first-week action with how many took it and how many of
 * those were active on day 30, against the people who did not. The lift is
 * printed only when both sides have ten people; otherwise the words "not
 * enough data" with the counts. The candidate is a correlation to test by
 * making the action easier, and the panel says so.
 */

const WINDOWS = [7, 30, 90] as const;

function SurfaceTable({ w }: { w: SurfaceFunnelWindow }) {
  const steps = w.rows[0]?.steps ?? [];
  return (
    <div className={s.card} style={{ padding: 0, overflowX: 'auto' }}>
      <table className={s.table} data-panel="surface-funnel">
        <thead>
          <tr>
            <th>Surface</th>
            <th style={{ textAlign: 'right' }}>Signed up</th>
            {steps.map((st) => <th key={st.milestone} style={{ textAlign: 'right' }}>{st.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {w.rows.map((r) => (
            <tr key={r.surface} data-surface={r.surface} className={r.surface === 'all' ? s.rowOn : undefined}>
              <td>
                {r.label}
                {r.small && <span className={s.muted} style={{ marginLeft: 6, fontSize: '0.74rem' }}>small, counts only</span>}
              </td>
              <td style={{ textAlign: 'right' }}>{num(r.cohort)}</td>
              {r.steps.map((st) => (
                <td key={st.milestone} style={{ textAlign: 'right' }} className={r.worst?.milestone === st.milestone ? s.funnelWorst : undefined}>
                  <div>{reachedCell(st, r.small)}</div>
                  <div className={s.sub}>{droppedCell(st, r.small)}</div>
                </td>
              ))}
            </tr>
          ))}
          {w.rows.length <= 1 && w.cohort === 0 && <tr><td colSpan={2 + steps.length} className={s.empty}>Nobody signed up in this window.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AhaTable({ o }: { o: AhaOutcome }) {
  const rows = sortActions(o.actions);
  return (
    <div className={s.card} style={{ padding: 0, overflowX: 'auto' }}>
      <table className={s.table} data-panel={`aha-d${o.day}`}>
        <thead>
          <tr>
            <th>First-week action</th>
            <th style={{ textAlign: 'right' }}>Did it: active on day {o.day}</th>
            <th style={{ textAlign: 'right' }}>Did not: active on day {o.day}</th>
            <th style={{ textAlign: 'right' }}>Lift</th>
            <th style={{ textAlign: 'right' }}>Of all who came back</th>
            <th>Read</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.action} data-action={a.action} className={o.candidate?.action === a.action ? s.rowOn : undefined}>
              <td>{a.label}{o.candidate?.action === a.action && <span className={s.muted} style={{ marginLeft: 6, fontSize: '0.74rem' }}>candidate</span>}</td>
              <td style={{ textAlign: 'right' }}>{sideCell(a.withRetained, a.withN, a.enough)}</td>
              <td style={{ textAlign: 'right' }}>{sideCell(a.withoutRetained, a.withoutN, a.enough)}</td>
              <td style={{ textAlign: 'right' }}>{liftCell(a)}</td>
              <td style={{ textAlign: 'right' }}>{a.recall === null ? NOT_ENOUGH : ratePct(a.recall)}</td>
              <td className={s.sub}>{confidenceCell(a)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ActivationPanels() {
  const q = useActivation();
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const [day, setDay] = useState<7 | 30>(30);
  const d = q.data;
  const w = d?.surfaces.find((x) => x.days === days) ?? null;
  const worst = w ? worstSurfaceStep(w) : null;
  const o = d ? d.aha[day === 30 ? 'd30' : 'd7'] : null;

  return (
    <>
      <Section
        title="Where people stop, per surface"
        summary={w ? `${num(w.cohort)} signed up in the last ${days} days${worst ? ` · worst loss: ${num(worst.dropped)} on ${worst.label} before ${worst.step}` : ''} · ${when(d?.generatedAt ?? null)}` : q.isError ? 'This panel needs GET /provider/admin/growth/activation.' : 'Counting.'}
        defaultOpen
        id="surface-funnel"
      >
        <div className={s.filters} role="tablist" aria-label="Surface funnel window" style={{ marginBottom: 10 }}>
          {WINDOWS.map((n) => (
            <button key={n} type="button" role="tab" aria-selected={days === n} className={`${s.filter} ${days === n ? s.filterOn : ''}`} onClick={() => setDays(n)}>{n} days</button>
          ))}
          {d && <span className={s.muted} style={{ fontSize: '0.78rem' }}>{num(d.people)} member accounts; {num(d.excluded.team)} team and {num(d.excluded.qa)} QA accounts left out</span>}
        </div>
        {w && <SurfaceTable w={w} />}
        {q.isError && <div className={s.error}>GET /provider/admin/growth/activation did not load. Nothing here is zero; the funnel is {NOT_ENOUGH} right now.</div>}
        <div className={s.muted} style={{ fontSize: '0.78rem', marginTop: 8 }}>
          The surface is the one they signed up on (the first session, else the consent row, else the first-check attempt); a browser on a phone is its own row because it is its own code path. Each cell is people who reached the step; a surface with under ten signups shows counts without a percent. The shaded cell is that surface's biggest loss.
        </div>
        {d && d.unavailable.length > 0 && <div className={s.jWarn} style={{ marginTop: 10 }}>Not read this time: {d.unavailable.map((u) => u.source).join(', ')}. The counts leave those out rather than guess.</div>}
      </Section>

      <Section
        title="What predicts coming back"
        summary={o ? `${num(o.eligible)} people with a closed day ${day} window · ${num(o.retained)} active then${o.baseRate !== null && o.enough ? ` (${ratePct(o.baseRate)})` : ''}` : q.isError ? 'Needs the same endpoint.' : 'Counting.'}
        defaultOpen
        id="aha"
      >
        <div className={s.filters} role="tablist" aria-label="Retention day" style={{ marginBottom: 10 }}>
          {([30, 7] as const).map((n) => (
            <button key={n} type="button" role="tab" aria-selected={day === n} className={`${s.filter} ${day === n ? s.filterOn : ''}`} onClick={() => setDay(n)}>active on day {n}</button>
          ))}
        </div>
        {o && <div className={o.candidate ? s.notice : s.empty} data-panel="aha-note">{o.note}</div>}
        {o && <div style={{ marginTop: 10 }}><AhaTable o={o} /></div>}
        <div className={s.muted} style={{ fontSize: '0.78rem', marginTop: 8 }}>
          Active on day {day} means a check or an Active Mode session in that day's window, the ledger's definition. A lift needs ten people on each side and thirty with a closed window; below that the words say so. "Of all who came back" is how many of the retained people did the action: a high number with a high lift is the a-ha candidate. It is a correlation; the test is to make that action easier for the next cohort and watch whether day {day} moves.
        </div>
      </Section>
    </>
  );
}
